import type { BlogArticlePage } from '$models/Page/BlogArticlePage';
import { buildPersonSchema } from '$lib/seo/schemas/person';
import { sanitizeHtmlToPlainText } from '$lib/utils';

/**
 * BlogPosting schema.org object for a blog article built from flexible content blocks.
 * @see https://developers.google.com/search/docs/appearance/structured-data/article
 */
export function buildBlogArticleSchema(
	page: BlogArticlePage,
	url: URL,
	siteName: string
): Record<string, unknown> {
	const pageUrl = `${url.origin}${url.pathname}`;
	const articleBody = page.blocks
		.map((block) =>
			'content' in block ? sanitizeHtmlToPlainText(block.content.replace(/</g, ' <')) : ''
		)
		.filter(Boolean)
		.join('\n\n');
	const image = page.heroImage ?? page.featuredImage;

	return {
		'@context': 'https://schema.org',
		'@type': 'BlogPosting',
		'@id': `${pageUrl}#article`,
		mainEntityOfPage: { '@id': pageUrl },
		headline: sanitizeHtmlToPlainText(page.title),
		description: page.metadata.description || undefined,
		image: image?.url,
		datePublished: `${page.dateGmt}Z`,
		dateModified: `${page.modifiedGmt}Z`,
		articleBody: articleBody || undefined,
		wordCount: articleBody ? articleBody.split(/\s+/).length : undefined,
		author: page.author?.url
			? buildPersonSchema(page.author, `${url.origin}${page.author.url}`)
			: undefined,
		publisher: { '@type': 'Organization', '@id': `${url.origin}/#organization`, name: siteName }
	};
}
